/**
 * ValidationStepper - Unified validation workflow component
 *
 * Guides the validator through a clear sequential flow:
 * 1. Execute Phase 1 (indication + criteria generation)
 * 2. Evaluate Phase 1 results (blind evaluation)
 * 3. Configure & Execute Phase 2 (web search - auto-runs after config)
 * 4. Evaluate Phase 2 results (search evaluation)
 * 5. View Combined Analysis
 *
 * Key UX improvements:
 * - Single linear flow with clear "Next Step" actions
 * - No need to navigate away and find the test run again
 * - Progress indicator shows where you are in the process
 * - Each step shows what's needed before proceeding
 * - Search execution is automatic after configuration
 */

import { useState, useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import {
  Play,
  CheckCircle,
  Loader2,
  Eye,
  Search,
  BarChart3,
  ArrowRight,
  Trophy,
  AlertCircle,
  Clock,
  Info,
  Activity,
  RotateCcw,
  Star,
} from 'lucide-react';
import { clsx } from 'clsx';
import { api, SearchExecution } from '../../../lib/api';
import { Modal } from '../../../components/ui/Modal';

type RunType = 'MODEL_COMPARISON' | 'MODEL_STABILITY';

interface ValidationStepperProps {
  testRunId: string;
  testRun: {
    id: string;
    name: string;
    status: string;
    runType: RunType;
    isRevealed: boolean;
    totalExecutions?: number;
    searchPhaseStatus?: 'NOT_STARTED' | 'READY' | 'RUNNING' | 'COMPLETED' | 'EVALUATED';
    config: {
      models: string[];
      iterations: number;
    };
    executions: Array<{
      id: string;
      status: string;
      blindCode: string;
    }>;
    evaluationSets: Array<{
      id: string;
      completedAt?: string;
    }>;
    // Stability evaluations for MODEL_STABILITY runs
    stabilityEvaluations?: Array<{
      id: string;
      phase: string;
      verdict: string;
      completedAt?: string;
    }>;
    // Quality assessment sets for Phase 5
    qualityAssessmentSets?: Array<{
      id: string;
      completedAt?: string;
    }>;
  };
  onRefetch: () => void;
}

type Step = 'phase1-execute' | 'phase1-evaluate' | 'phase2-search' | 'phase2-evaluate' | 'quality-assessment' | 'analysis';

// Steps for MODEL_COMPARISON
const COMPARISON_STEPS: { id: Step; label: string; shortLabel: string; description: string; phase?: string }[] = [
  { id: 'phase1-execute', label: 'Case Analysis', shortLabel: 'Case Analysis', description: 'Generate case analysis & matching criteria', phase: 'Phase 2+3' },
  { id: 'phase1-evaluate', label: 'Quality Evaluation', shortLabel: 'Evaluation', description: 'Evaluate & rank model outputs (blind)', phase: 'Phase 2+3' },
  { id: 'phase2-search', label: 'Care Provider Matching', shortLabel: 'Matching', description: 'Match care providers to matching criteria', phase: 'Phase 4' },
  { id: 'phase2-evaluate', label: 'Matching Evaluation', shortLabel: 'Evaluation', description: 'Evaluate & rank matches (blind)', phase: 'Phase 4' },
  { id: 'quality-assessment', label: 'Provider Quality Assessment', shortLabel: 'Quality', description: 'Assess recommended providers quality', phase: 'Phase 5' },
  { id: 'analysis', label: 'Results & Comparison', shortLabel: 'Results', description: 'Reveal models & compare performance' },
];

// Steps for MODEL_STABILITY (no quality assessment - only for comparison mode)
const STABILITY_STEPS: { id: Step; label: string; shortLabel: string; description: string; phase?: string }[] = [
  { id: 'phase1-execute', label: 'Case Analysis', shortLabel: 'Case Analysis', description: 'Run multiple iterations', phase: 'Phase 2+3' },
  { id: 'phase1-evaluate', label: 'Consistency Evaluation', shortLabel: 'Evaluation', description: 'Evaluate consistency across iterations', phase: 'Phase 2+3' },
  { id: 'phase2-search', label: 'Care Provider Matching', shortLabel: 'Matching', description: 'Run matching across iterations', phase: 'Phase 4' },
  { id: 'phase2-evaluate', label: 'Matching Consistency', shortLabel: 'Evaluation', description: 'Evaluate provider consistency', phase: 'Phase 4' },
  { id: 'analysis', label: 'Stability Results', shortLabel: 'Results', description: 'Reveal & analyze consistency' },
];

// Helper to get steps based on run type
const getStepsForRunType = (runType: RunType) =>
  runType === 'MODEL_STABILITY' ? STABILITY_STEPS : COMPARISON_STEPS;

const MODEL_DISPLAY: Record<string, { name: string; color: string }> = {
  CLAUDE_OPUS_4_5: { name: 'Claude Opus 4.5', color: 'bg-purple-100 text-purple-700' },
  CLAUDE_OPUS_4_6: { name: 'Claude Opus 4.6', color: 'bg-violet-100 text-violet-700' },
  GPT_5_2_PRO: { name: 'GPT-5.2 Pro', color: 'bg-green-100 text-green-700' },
  GEMINI_3_PRO: { name: 'Gemini 3 Pro', color: 'bg-blue-100 text-blue-700' },
  GRAND_ROUNDS: { name: 'Grand Rounds', color: 'bg-indigo-100 text-indigo-700' },
  BETTERDOC: { name: 'BetterDoc', color: 'bg-teal-100 text-teal-700' },
};

function getStepStatus(
  step: Step,
  testRun: ValidationStepperProps['testRun'],
  phase1EvaluationComplete: boolean,
  phase2EvaluationComplete: boolean,
  qualityAssessmentComplete: boolean
): 'completed' | 'current' | 'upcoming' | 'blocked' {
  const phase1Complete = testRun.status === 'COMPLETED';
  const searchPhaseStatus = testRun.searchPhaseStatus || 'NOT_STARTED';

  switch (step) {
    case 'phase1-execute':
      if (phase1Complete) return 'completed';
      if (testRun.status === 'RUNNING') return 'current';
      return 'current';

    case 'phase1-evaluate':
      if (!phase1Complete) return 'blocked';
      if (phase1EvaluationComplete) return 'completed';
      return 'current';

    case 'phase2-search':
      // Combined configure + execute step
      if (!phase1EvaluationComplete) return 'blocked';
      if (searchPhaseStatus === 'COMPLETED' || searchPhaseStatus === 'EVALUATED') return 'completed';
      // Show as current if not started, ready, or running
      return 'current';

    case 'phase2-evaluate':
      if (searchPhaseStatus !== 'COMPLETED' && searchPhaseStatus !== 'EVALUATED') return 'blocked';
      if (phase2EvaluationComplete || searchPhaseStatus === 'EVALUATED') return 'completed';
      return 'current';

    case 'quality-assessment':
      // Quality assessment can start after Phase 4 (matching) is evaluated
      if (!phase2EvaluationComplete && searchPhaseStatus !== 'EVALUATED') return 'blocked';
      if (qualityAssessmentComplete) return 'completed';
      return 'current';

    case 'analysis':
      // Analysis requires quality assessment to be complete
      if (!qualityAssessmentComplete) return 'blocked';
      if (testRun.isRevealed) return 'completed';
      return 'current';

    default:
      return 'upcoming';
  }
}

// Horizontal step indicator (top layout, similar to TestCaseWizard)
function HorizontalStepIndicator({
  steps,
  activeStep,
  getStatus,
  onStepClick,
  runType,
}: {
  steps: typeof COMPARISON_STEPS;
  activeStep: Step;
  getStatus: (step: Step) => 'completed' | 'current' | 'upcoming' | 'blocked';
  onStepClick: (step: Step) => void;
  runType: RunType;
}) {
  const completedCount = steps.filter(s => getStatus(s.id) === 'completed').length;
  const percentage = Math.round((completedCount / steps.length) * 100);

  return (
    <div className="space-y-4">
      {/* Progress summary with bar */}
      <div className="flex items-center gap-4">
        <div className="flex-1">
          <div className="flex items-center justify-between text-sm mb-1.5">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-gray-800">
                {runType === 'MODEL_STABILITY' ? 'Stability Validation Workflow' : 'Model Validation Workflow'}
              </span>
              {runType === 'MODEL_STABILITY' && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                  Stability Test
                </span>
              )}
            </div>
            <span className="text-gray-500">{completedCount}/{steps.length} complete ({percentage}%)</span>
          </div>
          <div className="h-2 w-full rounded-full bg-gray-200 overflow-hidden">
            <div
              className={clsx(
                'h-2 rounded-full transition-all duration-500',
                runType === 'MODEL_STABILITY' ? 'bg-purple-500' : 'bg-green-500'
              )}
              style={{ width: `${percentage}%` }}
            />
          </div>
        </div>
      </div>

      {/* Step indicator with explicit labels */}
      <nav aria-label="Progress">
        <ol className="flex items-start justify-between gap-2">
          {steps.map((step, index) => {
            const status = getStatus(step.id);
            const isActive = activeStep === step.id;
            const canClick = status === 'completed' || status === 'current';

            return (
              <li key={step.id} className="relative flex-1 min-w-0">
                {/* Connecting line */}
                {index > 0 && (
                  <div
                    className={clsx(
                      'absolute top-4 left-0 right-1/2 h-0.5 -translate-y-1/2 transition-colors',
                      status === 'completed' || (status === 'current' && getStatus(steps[index - 1].id) === 'completed')
                        ? 'bg-green-500'
                        : 'bg-gray-200'
                    )}
                  />
                )}
                {index < steps.length - 1 && (
                  <div
                    className={clsx(
                      'absolute top-4 left-1/2 right-0 h-0.5 -translate-y-1/2 transition-colors',
                      status === 'completed' ? 'bg-green-500' : 'bg-gray-200'
                    )}
                  />
                )}

                <button
                  onClick={() => canClick && onStepClick(step.id)}
                  disabled={!canClick}
                  className={clsx(
                    'relative w-full flex flex-col items-center text-center group',
                    canClick ? 'cursor-pointer' : 'cursor-not-allowed'
                  )}
                >
                  {/* Step circle */}
                  <div
                    className={clsx(
                      'flex h-8 w-8 items-center justify-center rounded-full border-2 bg-white z-10 transition-all',
                      status === 'completed' && 'border-green-500 bg-green-500',
                      status === 'current' && isActive && 'border-amber-500 ring-4 ring-amber-100 bg-amber-500',
                      status === 'current' && !isActive && 'border-amber-500 group-hover:ring-2 group-hover:ring-amber-200',
                      (status === 'blocked' || status === 'upcoming') && 'border-gray-300 opacity-50'
                    )}
                  >
                    {status === 'completed' ? (
                      <CheckCircle className="h-4 w-4 text-white" />
                    ) : (
                      <span
                        className={clsx(
                          'text-xs font-bold',
                          status === 'current' && isActive && 'text-white',
                          status === 'current' && !isActive && 'text-amber-600',
                          (status === 'blocked' || status === 'upcoming') && 'text-gray-400'
                        )}
                      >
                        {index + 1}
                      </span>
                    )}
                  </div>

                  {/* Phase label */}
                  {step.phase && (
                    <span
                      className={clsx(
                        'mt-1.5 text-[10px] font-bold uppercase tracking-wide',
                        isActive && 'text-amber-600',
                        status === 'completed' && !isActive && 'text-green-600',
                        (status === 'blocked' || status === 'upcoming') && 'text-gray-300'
                      )}
                    >
                      {step.phase}
                    </span>
                  )}

                  {/* Step label - always visible */}
                  <span
                    className={clsx(
                      'mt-1 text-xs font-semibold leading-tight',
                      isActive && 'text-amber-700',
                      status === 'completed' && !isActive && 'text-green-700',
                      (status === 'blocked' || status === 'upcoming') && 'text-gray-400'
                    )}
                  >
                    {step.shortLabel}
                  </span>

                  {/* Step description - always visible */}
                  <span
                    className={clsx(
                      'mt-0.5 text-[10px] leading-tight max-w-[100px]',
                      isActive ? 'text-amber-600' : 'text-gray-400'
                    )}
                  >
                    {step.description}
                  </span>
                </button>
              </li>
            );
          })}
        </ol>
      </nav>
    </div>
  );
}

export function ValidationStepper({ testRunId, testRun, onRefetch }: ValidationStepperProps) {
  const queryClient = useQueryClient();
  const [showResetModal, setShowResetModal] = useState(false);

  // Determine evaluation status
  const completedExecutions = testRun.executions.filter((e) => e.status === 'COMPLETED').length;
  // For MODEL_STABILITY, check stabilityEvaluations instead of evaluationSets
  const phase1EvaluationComplete =
    testRun.runType === 'MODEL_STABILITY'
      ? testRun.stabilityEvaluations?.some((e) => e.phase === 'PHASE_1' && e.verdict) ?? false
      : testRun.evaluationSets.some((e) => e.completedAt);

  // Query for search phase data - poll when running
  const { data: searchPhaseData } = useQuery({
    queryKey: ['validation', 'search-phase', testRunId],
    queryFn: () => api.searchPhase.getPhase(testRunId),
    enabled: testRun.status === 'COMPLETED',
    refetchInterval: testRun.searchPhaseStatus === 'RUNNING' ? 2000 : false,
  });

  const searchPhase = searchPhaseData?.data;
  const searchExecutions = searchPhase?.searchExecutions || [];
  const completedSearches = searchExecutions.filter((se: SearchExecution) => se.status === 'COMPLETED').length;
  const totalSearches = searchExecutions.length;

  // Query for search evaluation status
  const { data: searchEvalsData } = useQuery({
    queryKey: ['validation', 'search-evaluations', testRunId],
    queryFn: () => api.searchEvaluations.list(testRunId),
    enabled: testRun.searchPhaseStatus === 'COMPLETED' || testRun.searchPhaseStatus === 'EVALUATED',
  });

  // For MODEL_STABILITY, check stabilityEvaluations PHASE_2 instead of searchEvalsData
  const phase2EvaluationComplete =
    testRun.searchPhaseStatus === 'EVALUATED' ||
    (testRun.runType === 'MODEL_STABILITY'
      ? testRun.stabilityEvaluations?.some((e) => e.phase === 'PHASE_2' && e.verdict) ?? false
      : (searchEvalsData?.data || []).some((e: { completedAt?: string }) => e.completedAt));

  // Check if quality assessment is complete (Phase 5) - only for comparison mode
  const qualityAssessmentComplete =
    testRun.runType === 'MODEL_STABILITY'
      ? true // Skip quality assessment for stability mode
      : testRun.qualityAssessmentSets?.some((e) => e.completedAt) ?? false;

  // Calculate current step based on state
  const computedStep = (() => {
    if (testRun.status !== 'COMPLETED') return 'phase1-execute';
    if (!phase1EvaluationComplete) return 'phase1-evaluate';
    // Combined search step: covers NOT_STARTED, READY, and RUNNING
    if (!testRun.searchPhaseStatus || testRun.searchPhaseStatus === 'NOT_STARTED' ||
        testRun.searchPhaseStatus === 'READY' || testRun.searchPhaseStatus === 'RUNNING') return 'phase2-search';
    if (testRun.searchPhaseStatus === 'COMPLETED' && !phase2EvaluationComplete) return 'phase2-evaluate';
    // Quality assessment step (Phase 5) - only for comparison mode
    if (testRun.runType !== 'MODEL_STABILITY' && !qualityAssessmentComplete) return 'quality-assessment';
    return 'analysis';
  })() as Step;

  const [activeStep, setActiveStep] = useState<Step>(computedStep);
  const [topN, setTopN] = useState(3);
  // Default to AI models + Grand Rounds for Phase 4 matching (BetterDoc disabled by default)
  const [selectedSearchModels, setSelectedSearchModels] = useState<string[]>([
    'CLAUDE_OPUS_4_6', 'GPT_5_2_PRO', 'GEMINI_3_PRO', 'GRAND_ROUNDS'
  ]);

  // Use a ref to track user navigation without triggering re-renders
  const userNavigatedRef = useRef(false);
  const previousComputedStepRef = useRef<Step>(computedStep);

  // Define step order based on run type (stability mode skips quality-assessment)
  const stepOrder: Step[] = testRun.runType === 'MODEL_STABILITY'
    ? ['phase1-execute', 'phase1-evaluate', 'phase2-search', 'phase2-evaluate', 'analysis']
    : ['phase1-execute', 'phase1-evaluate', 'phase2-search', 'phase2-evaluate', 'quality-assessment', 'analysis'];
  const getStepIndex = (step: Step) => stepOrder.indexOf(step);

  // Auto-advance only when computedStep changes (workflow progresses)
  useEffect(() => {
    // Only react to actual computedStep changes
    if (computedStep === previousComputedStepRef.current) {
      return;
    }

    const prevIndex = getStepIndex(previousComputedStepRef.current);
    const newIndex = getStepIndex(computedStep);

    // Update ref to track the new computed step
    previousComputedStepRef.current = computedStep;

    // If workflow advanced forward
    if (newIndex > prevIndex) {
      // If user hasn't manually navigated, or if the workflow passed their current view, auto-advance
      if (!userNavigatedRef.current || newIndex > getStepIndex(activeStep)) {
        setActiveStep(computedStep);
        userNavigatedRef.current = false;
      }
    }
  }, [computedStep, activeStep]);

  // Handle user clicking on a step
  const handleStepClick = (step: Step) => {
    userNavigatedRef.current = true;
    setActiveStep(step);
  };

  // Mutations
  const startPhase1Mutation = useMutation({
    mutationFn: () => api.post(`/api/validation/test-runs/${testRunId}/start`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['validation', 'test-run', testRunId] });
      onRefetch();
    },
  });

  const startSearchPhaseMutation = useMutation({
    // Pass selected search models explicitly (including GRAND_ROUNDS if selected)
    // BETTERDOC is handled separately via includeBetterDoc flag
    mutationFn: () => {
      const includeBetterDoc = selectedSearchModels.includes('BETTERDOC');
      const searchModels = selectedSearchModels.filter((m) => m !== 'BETTERDOC');
      return api.searchPhase.start(testRunId, { topN, searchModels, includeBetterDoc });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['validation', 'search-phase', testRunId] });
      queryClient.invalidateQueries({ queryKey: ['validation', 'test-run', testRunId] });
      onRefetch(); // Trigger parent to refetch test run data so searchPhaseStatus updates
    },
  });

  const executeSearchMutation = useMutation({
    mutationFn: () => api.searchPhase.execute(testRunId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['validation', 'search-phase', testRunId] });
      queryClient.invalidateQueries({ queryKey: ['validation', 'test-run', testRunId] });
      onRefetch();
    },
  });

  const resetSearchMutation = useMutation({
    mutationFn: () => api.searchPhase.reset(testRunId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['validation', 'search-phase', testRunId] });
      queryClient.invalidateQueries({ queryKey: ['validation', 'test-run', testRunId] });
      onRefetch();
    },
  });

  // Render step content
  const renderStepContent = () => {
    switch (activeStep) {
      case 'phase1-execute':
        return (
          <Phase1ExecuteContent
            testRun={testRun}
            completedExecutions={completedExecutions}
            onStart={() => startPhase1Mutation.mutate()}
            isStarting={startPhase1Mutation.isPending}
            onNext={() => setActiveStep('phase1-evaluate')}
          />
        );

      case 'phase1-evaluate':
        return (
          <Phase1EvaluateContent
            testRunId={testRunId}
            isComplete={phase1EvaluationComplete}
            runType={testRun.runType}
            onNext={() => setActiveStep('phase2-search')}
          />
        );

      case 'phase2-search':
        return (
          <Phase2SearchContent
            testRun={testRun}
            searchPhaseStatus={testRun.searchPhaseStatus}
            topN={topN}
            setTopN={setTopN}
            selectedSearchModels={selectedSearchModels}
            setSelectedSearchModels={setSelectedSearchModels}
            onConfigureAndStart={async () => {
              // Configure the search phase
              await startSearchPhaseMutation.mutateAsync();
              // Auto-execute after configuration
              await executeSearchMutation.mutateAsync();
            }}
            isConfiguring={startSearchPhaseMutation.isPending}
            isExecuting={executeSearchMutation.isPending}
            completedSearches={completedSearches}
            totalSearches={totalSearches}
            error={startSearchPhaseMutation.error || executeSearchMutation.error}
            onNext={() => setActiveStep('phase2-evaluate')}
            onReset={() => setShowResetModal(true)}
            isResetting={resetSearchMutation.isPending}
            resetError={resetSearchMutation.error}
          />
        );

      case 'phase2-evaluate':
        return (
          <Phase2EvaluateContent
            testRunId={testRunId}
            isComplete={phase2EvaluationComplete}
            runType={testRun.runType}
            onNext={() => setActiveStep(testRun.runType === 'MODEL_STABILITY' ? 'analysis' : 'quality-assessment')}
          />
        );

      case 'quality-assessment':
        return (
          <Phase5QualityAssessmentContent
            testRunId={testRunId}
            isComplete={qualityAssessmentComplete}
            onNext={() => setActiveStep('analysis')}
          />
        );

      case 'analysis':
        return (
          <AnalysisContent
            testRunId={testRunId}
            isRevealed={testRun.isRevealed}
            runType={testRun.runType}
            onReveal={onRefetch}
          />
        );

      default:
        return null;
    }
  };

  // Get the appropriate steps for this run type
  const steps = getStepsForRunType(testRun.runType);

  return (
    <div className="space-y-6">
      {/* Top horizontal step indicator */}
      <div className="rounded-xl border border-gray-200 bg-white p-6">
        <HorizontalStepIndicator
          steps={steps}
          activeStep={activeStep}
          getStatus={(step) => getStepStatus(step, testRun, phase1EvaluationComplete, phase2EvaluationComplete, qualityAssessmentComplete)}
          onStepClick={handleStepClick}
          runType={testRun.runType}
        />
      </div>

      {/* Main content area */}
      <div>{renderStepContent()}</div>

      {/* Reset Confirmation Modal */}
      <Modal
        isOpen={showResetModal}
        onClose={() => setShowResetModal(false)}
        title="Reset Matching Phase?"
        size="sm"
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <div className="rounded-full bg-amber-100 p-2">
              <RotateCcw className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-gray-700">
                This will delete all matching results and allow you to start fresh.
              </p>
              <ul className="mt-2 text-xs text-gray-500 space-y-1">
                <li>• All search executions will be deleted</li>
                <li>• Any evaluation progress will be lost</li>
                <li>• You can reconfigure and rerun matching</li>
              </ul>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              onClick={() => setShowResetModal(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                resetSearchMutation.mutate();
                setShowResetModal(false);
              }}
              disabled={resetSearchMutation.isPending}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-amber-600 rounded-lg hover:bg-amber-700 disabled:opacity-50 transition-colors"
            >
              {resetSearchMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RotateCcw className="h-4 w-4" />
              )}
              Reset & Retry
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ============================================================================
// Step Content Components
// ============================================================================

// Detailed progress messages that rotate during AI processing
const PROGRESS_STAGES = [
  { message: 'Reading and understanding patient case details...', icon: '📋' },
  { message: 'Analyzing medical condition and symptoms...', icon: '🔬' },
  { message: 'Evaluating patient history and context...', icon: '📊' },
  { message: 'Generating indication assessment...', icon: '✍️' },
  { message: 'Determining referral appropriateness...', icon: '🎯' },
  { message: 'Creating prioritized matching criteria...', icon: '🔍' },
  { message: 'Identifying critical care provider requirements...', icon: '⚡' },
  { message: 'Validating output quality and completeness...', icon: '✅' },
];

function ProgressMessages({
  completedExecutions,
  totalExecutions,
  runType,
}: {
  completedExecutions: number;
  totalExecutions: number;
  runType: RunType;
}) {
  const [stageIndex, setStageIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setStageIndex((prev) => (prev + 1) % PROGRESS_STAGES.length);
    }, 3000); // Rotate every 3 seconds

    return () => clearInterval(interval);
  }, []);

  const currentStage = PROGRESS_STAGES[stageIndex];
  const currentExecution = completedExecutions + 1;

  return (
    <div className="mt-4 space-y-3">
      {/* Current execution indicator */}
      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center gap-2 text-blue-700">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="font-medium">
            Processing execution {currentExecution} of {totalExecutions}
          </span>
        </div>
        <span className="text-gray-500">
          {totalExecutions > 0 ? Math.round((completedExecutions / totalExecutions) * 100) : 0}% complete
        </span>
      </div>

      {/* Detailed status message */}
      <div className="rounded-lg bg-blue-50 border border-blue-100 p-3">
        <div className="flex items-center gap-3">
          <span className="text-xl" role="img" aria-label="status">
            {currentStage.icon}
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-blue-900 truncate">
              {currentStage.message}
            </p>
            <p className="text-xs text-blue-600 mt-0.5">
              {runType === 'MODEL_STABILITY'
                ? 'Each iteration analyzes the case independently to measure consistency'
                : 'Each model analyzes the case independently to ensure unbiased results'}
            </p>
          </div>
        </div>
      </div>

      {/* Helpful tip */}
      <div className="flex items-start gap-2 text-xs text-gray-500">
        <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
        <span>
          You can safely leave this page and return later — progress is saved automatically.
        </span>
      </div>
    </div>
  );
}

// Detailed progress messages for web search phase
const SEARCH_PROGRESS_STAGES = [
  { message: 'Preparing matching criteria from evaluation rankings...', icon: '📋' },
  { message: 'Constructing optimized matching queries...', icon: '🔧' },
  { message: 'Matching Swiss healthcare provider databases...', icon: '🏥' },
  { message: 'Querying medical specialty directories...', icon: '📚' },
  { message: 'Filtering providers by location and specialty...', icon: '📍' },
  { message: 'Verifying provider credentials and availability...', icon: '✓' },
  { message: 'Ranking results by relevance to criteria...', icon: '🎯' },
  { message: 'Compiling and anonymizing matching results...', icon: '🔒' },
];

function SearchProgressMessages({
  completedSearches,
  totalSearches,
  runType,
}: {
  completedSearches: number;
  totalSearches: number;
  runType: RunType;
}) {
  const [stageIndex, setStageIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setStageIndex((prev) => (prev + 1) % SEARCH_PROGRESS_STAGES.length);
    }, 3000); // Rotate every 3 seconds

    return () => clearInterval(interval);
  }, []);

  const currentStage = SEARCH_PROGRESS_STAGES[stageIndex];
  const currentSearch = completedSearches + 1;

  return (
    <div className="mt-4 space-y-3">
      {/* Current search indicator */}
      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center gap-2 text-blue-700">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="font-medium">
            Running match {currentSearch} of {totalSearches}
          </span>
        </div>
        <span className="text-gray-500">
          {totalSearches > 0 ? Math.round((completedSearches / totalSearches) * 100) : 0}% complete
        </span>
      </div>

      {/* Detailed status message */}
      <div className="rounded-lg bg-blue-50 border border-blue-100 p-3">
        <div className="flex items-center gap-3">
          <span className="text-xl" role="img" aria-label="status">
            {currentStage.icon}
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-blue-900 truncate">
              {currentStage.message}
            </p>
            <p className="text-xs text-blue-600 mt-0.5">
              {runType === 'MODEL_STABILITY'
                ? 'Each iteration searches independently using your ranked criteria'
                : 'Each model searches independently using your ranked criteria'}
            </p>
          </div>
        </div>
      </div>

      {/* Helpful tip */}
      <div className="flex items-start gap-2 text-xs text-gray-500">
        <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
        <span>
          You can safely leave this page and return later — progress is saved automatically.
        </span>
      </div>
    </div>
  );
}

function Phase1ExecuteContent({
  testRun,
  completedExecutions,
  onStart,
  isStarting,
  onNext,
}: {
  testRun: ValidationStepperProps['testRun'];
  completedExecutions: number;
  onStart: () => void;
  isStarting: boolean;
  onNext: () => void;
}) {
  // Calculate total executions: models × iterations
  const totalExecutions = testRun.executions.length
    || testRun.totalExecutions
    || (testRun.config.models.length * testRun.config.iterations)
    || 1; // Fallback to 1 to avoid division by zero
  const isRunning = testRun.status === 'RUNNING';
  const isComplete = testRun.status === 'COMPLETED';
  const canStart = testRun.status === 'DRAFT' || testRun.status === 'PENDING';

  return (
    <div className="space-y-4">
      {/* Step header card */}
      <div className="rounded-xl border border-gray-200 bg-white p-6">
        <div className="flex items-start gap-4">
          <div className={clsx('rounded-lg p-3', isComplete ? 'bg-green-100' : isRunning ? 'bg-blue-100' : 'bg-amber-100')}>
            {isComplete ? (
              <CheckCircle className="h-6 w-6 text-green-600" />
            ) : isRunning ? (
              <Loader2 className="h-6 w-6 text-blue-600 animate-spin" />
            ) : (
              <Play className="h-6 w-6 text-amber-600" />
            )}
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-bold text-gray-900">Step 1: Case Analysis (Phase 2+3)</h2>
            <p className="mt-1 text-gray-600">
              {testRun.runType === 'MODEL_STABILITY'
                ? 'The AI model will analyze the patient case multiple times to evaluate consistency.'
                : 'Each AI model will analyze the patient case and generate indication assessments + matching criteria.'}
            </p>
          </div>
        </div>

        {/* What happens in this step */}
        <div className="mt-6 rounded-lg border p-4 bg-blue-50 border-blue-100">
          <div className="flex items-start gap-2">
            <Info className="h-5 w-5 mt-0.5 flex-shrink-0 text-blue-600" />
            <div>
              <h3 className="text-sm font-semibold text-blue-900">What happens in this step</h3>
              <ul className="mt-2 space-y-1 text-sm text-blue-800">
                {testRun.runType === 'MODEL_STABILITY' ? (
                  <>
                    <li>• The model analyzes the patient's medical condition multiple times</li>
                    <li>• Generates an indication assessment (is referral appropriate?)</li>
                    <li>• Creates prioritized matching criteria for finding care providers</li>
                    <li>• Results are assigned anonymous codes (Run 1, Run 2, etc.) to evaluate consistency</li>
                  </>
                ) : (
                  <>
                    <li>• Each model analyzes the patient's medical condition</li>
                    <li>• Generates an indication assessment (is referral appropriate?)</li>
                    <li>• Creates prioritized matching criteria for finding care providers</li>
                    <li>• Results are assigned anonymous codes (Model A, Model B, etc.)</li>
                  </>
                )}
              </ul>
            </div>
          </div>
        </div>

        {/* Configuration summary */}
        <div className="mt-4 rounded-lg bg-gray-50 p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Test Configuration</h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Models being tested:</span>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {testRun.config.models.map((model) => {
                  const config = MODEL_DISPLAY[model];
                  return (
                    <span key={model} className={clsx('px-2.5 py-1 rounded-md text-xs font-medium', config?.color || 'bg-gray-100 text-gray-700')}>
                      {config?.name || model}
                    </span>
                  );
                })}
              </div>
            </div>
            <div>
              <span className="text-gray-500">Iterations per model:</span>
              <p className="mt-1 font-semibold text-gray-900">{testRun.config.iterations}×</p>
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-gray-200">
            <span className="text-gray-500 text-sm">Total executions: </span>
            <span className="font-bold text-gray-900">{totalExecutions}</span>
            <span className="text-gray-400 text-sm"> ({testRun.config.models.length} models × {testRun.config.iterations} iterations)</span>
          </div>
        </div>

        {/* Progress */}
        {(isRunning || isComplete) && (
          <div className="mt-4 rounded-lg border border-gray-200 p-4">
            <div className="flex justify-between text-sm mb-2">
              <span className="font-medium text-gray-700">Execution Progress</span>
              <span className={clsx('font-semibold', isComplete ? 'text-green-600' : 'text-amber-600')}>
                {completedExecutions} / {totalExecutions}
              </span>
            </div>
            <div className="h-3 w-full rounded-full bg-gray-200 overflow-hidden">
              <div
                className={clsx(
                  'h-3 rounded-full transition-all duration-500',
                  isComplete ? 'bg-green-500' : 'bg-amber-500'
                )}
                style={{ width: `${totalExecutions > 0 ? (completedExecutions / totalExecutions) * 100 : 0}%` }}
              />
            </div>
            {isRunning && (
              <ProgressMessages completedExecutions={completedExecutions} totalExecutions={totalExecutions} runType={testRun.runType} />
            )}
            {isComplete && (
              <div className="mt-3 flex items-center gap-2 text-sm text-green-600">
                <CheckCircle className="h-4 w-4" />
                <span>All executions complete! Ready for evaluation.</span>
              </div>
            )}
          </div>
        )}

        {/* Action section */}
        <div className="mt-6 pt-4 border-t border-gray-200">
          {canStart && (
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-900">Ready to start?</p>
                <p className="text-xs text-gray-500">Click the button to begin case analysis</p>
              </div>
              <button
                onClick={onStart}
                disabled={isStarting}
                className="inline-flex items-center gap-2 rounded-lg bg-green-600 px-6 py-3 text-sm font-semibold text-white shadow-sm hover:bg-green-700 disabled:opacity-50 transition-colors"
              >
                {isStarting ? <Loader2 className="h-5 w-5 animate-spin" /> : <Play className="h-5 w-5" />}
                Start Case Analysis
              </button>
            </div>
          )}
          {isRunning && (
            <div className="flex items-center gap-3 rounded-lg bg-blue-50 border border-blue-200 p-4">
              <Clock className="h-5 w-5 text-blue-600" />
              <div>
                <p className="text-sm font-medium text-blue-900">Analysis in progress</p>
                <p className="text-xs text-blue-700">Please wait while the AI models process the case. You can leave this page and come back.</p>
              </div>
            </div>
          )}
          {isComplete && (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-green-600">
                <CheckCircle className="h-5 w-5" />
                <span className="font-medium">Step complete!</span>
              </div>
              <button
                onClick={onNext}
                className="inline-flex items-center gap-2 rounded-lg bg-amber-600 px-6 py-3 text-sm font-semibold text-white shadow-sm hover:bg-amber-700 transition-colors"
              >
                Continue to Evaluation
                <ArrowRight className="h-5 w-5" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Phase1EvaluateContent({
  testRunId,
  isComplete,
  runType,
  onNext,
}: {
  testRunId: string;
  isComplete: boolean;
  runType: RunType;
  onNext: () => void;
}) {
  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-gray-200 bg-white p-6">
        <div className="flex items-start gap-4">
          <div className={clsx('rounded-lg p-3', isComplete ? 'bg-green-100' : 'bg-amber-100')}>
            {isComplete ? (
              <CheckCircle className="h-6 w-6 text-green-600" />
            ) : (
              <Eye className="h-6 w-6 text-amber-600" />
            )}
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-bold text-gray-900">Step 2: Evaluate AI Outputs</h2>
            <p className="mt-1 text-gray-600">
              {runType === 'MODEL_STABILITY'
                ? 'Review and evaluate the AI-generated analyses from different iterations. You won\'t know which iteration created each one (blind evaluation).'
                : 'Review and rank the AI-generated analyses. You won\'t know which model created each one (blind evaluation).'}
            </p>
          </div>
        </div>

        {/* What you need to do */}
        <div className="mt-6 rounded-lg border p-4 bg-amber-50 border-amber-100">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-5 w-5 mt-0.5 flex-shrink-0 text-amber-600" />
            <div>
              <h3 className="text-sm font-semibold text-amber-900">Your task in this step</h3>
              <p className="mt-1 text-sm text-amber-800">
                {runType === 'MODEL_STABILITY'
                  ? 'Compare the outputs from different iterations (shown as "Run 1", "Run 2", etc.) and evaluate consistency across runs.'
                  : 'Compare the outputs from different models (shown as "Model A", "Model B", etc.) and rank them from best to worst based on quality.'}
              </p>
            </div>
          </div>
        </div>

        {/* Evaluation criteria */}
        <div className="mt-4 rounded-lg bg-gray-50 p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">
            {runType === 'MODEL_STABILITY'
              ? 'You\'ll evaluate consistency on:'
              : 'You\'ll evaluate each output on:'}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {runType === 'MODEL_STABILITY' ? (
              // Standard stability criteria
              <>
                <div className="flex items-start gap-2">
                  <div className="rounded-full bg-purple-100 p-1 mt-0.5">
                    <CheckCircle className="h-3 w-3 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-800">Suitability Agreement</p>
                    <p className="text-xs text-gray-500">Do all iterations agree on suitability?</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="rounded-full bg-purple-100 p-1 mt-0.5">
                    <CheckCircle className="h-3 w-3 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-800">Confidence Variance</p>
                    <p className="text-xs text-gray-500">How much do confidence scores vary?</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="rounded-full bg-purple-100 p-1 mt-0.5">
                    <CheckCircle className="h-3 w-3 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-800">Criteria Consistency</p>
                    <p className="text-xs text-gray-500">Are matching criteria the same across runs?</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="rounded-full bg-purple-100 p-1 mt-0.5">
                    <CheckCircle className="h-3 w-3 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-800">Overall Stability</p>
                    <p className="text-xs text-gray-500">Rate model consistency overall</p>
                  </div>
                </div>
              </>
            ) : (
              // Standard model comparison criteria
              <>
                <div className="flex items-start gap-2">
                  <div className="rounded-full bg-blue-100 p-1 mt-0.5">
                    <CheckCircle className="h-3 w-3 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-800">Clinical Accuracy</p>
                    <p className="text-xs text-gray-500">Is the indication assessment correct?</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="rounded-full bg-blue-100 p-1 mt-0.5">
                    <CheckCircle className="h-3 w-3 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-800">Reasoning Quality</p>
                    <p className="text-xs text-gray-500">Is the clinical reasoning sound?</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="rounded-full bg-blue-100 p-1 mt-0.5">
                    <CheckCircle className="h-3 w-3 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-800">Matching Criteria Relevance</p>
                    <p className="text-xs text-gray-500">Are the matching criteria appropriate?</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="rounded-full bg-blue-100 p-1 mt-0.5">
                    <CheckCircle className="h-3 w-3 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-800">Overall Ranking</p>
                    <p className="text-xs text-gray-500">Which output is best overall?</p>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Action section */}
        <div className="mt-6 pt-4 border-t border-gray-200">
          {isComplete ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-green-600">
                <CheckCircle className="h-5 w-5" />
                <span className="font-medium">Evaluation complete!</span>
              </div>
              <div className="flex items-center gap-3">
                <Link
                  to={runType === 'MODEL_STABILITY'
                    ? `/validation/test-runs/${testRunId}/stability-evaluate`
                    : `/validation/test-runs/${testRunId}/evaluate`}
                  className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 transition-colors"
                >
                  <Eye className="h-4 w-4" />
                  View Evaluation
                </Link>
                <button
                  onClick={onNext}
                  className="inline-flex items-center gap-2 rounded-lg bg-amber-600 px-6 py-3 text-sm font-semibold text-white shadow-sm hover:bg-amber-700 transition-colors"
                >
                  Continue to Matching Setup
                  <ArrowRight className="h-5 w-5" />
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-900">Ready to evaluate?</p>
                <p className="text-xs text-gray-500">
                  {runType === 'MODEL_STABILITY'
                    ? 'Evaluate consistency across iterations'
                    : 'Estimated time: 10-15 minutes'}
                </p>
              </div>
              <Link
                to={runType === 'MODEL_STABILITY'
                  ? `/validation/test-runs/${testRunId}/stability-evaluate`
                  : `/validation/test-runs/${testRunId}/evaluate`}
                className="inline-flex items-center gap-2 rounded-lg bg-amber-600 px-6 py-3 text-sm font-semibold text-white shadow-sm hover:bg-amber-700 transition-colors"
              >
                <Eye className="h-5 w-5" />
                {runType === 'MODEL_STABILITY' ? 'Start Stability Evaluation' : 'Start Evaluation'}
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// All available search models for Phase 4
const ALL_SEARCH_MODELS = [
  { id: 'CLAUDE_OPUS_4_6', name: 'Claude Opus 4.6', color: 'bg-violet-100 text-violet-700' },
  { id: 'CLAUDE_OPUS_4_5', name: 'Claude Opus 4.5', color: 'bg-purple-100 text-purple-700' },
  { id: 'GPT_5_2_PRO', name: 'GPT-5.2 Pro', color: 'bg-green-100 text-green-700' },
  { id: 'GEMINI_3_PRO', name: 'Gemini 3 Pro', color: 'bg-blue-100 text-blue-700' },
  { id: 'GRAND_ROUNDS', name: 'Grand Rounds', color: 'bg-indigo-100 text-indigo-700' },
  { id: 'BETTERDOC', name: 'BetterDoc', color: 'bg-teal-100 text-teal-700' },
];

function Phase2SearchContent({
  testRun,
  searchPhaseStatus,
  topN,
  setTopN,
  selectedSearchModels,
  setSelectedSearchModels,
  onConfigureAndStart,
  isConfiguring,
  isExecuting,
  completedSearches,
  totalSearches,
  error,
  onNext,
  onReset,
  isResetting,
  resetError,
}: {
  testRun: ValidationStepperProps['testRun'];
  searchPhaseStatus: ValidationStepperProps['testRun']['searchPhaseStatus'];
  topN: number;
  setTopN: (n: number) => void;
  selectedSearchModels: string[];
  setSelectedSearchModels: (models: string[]) => void;
  onConfigureAndStart: () => Promise<void>;
  isConfiguring: boolean;
  isExecuting: boolean;
  completedSearches: number;
  totalSearches: number;
  error: Error | null;
  onNext: () => void;
  onReset?: () => void;
  isResetting?: boolean;
  resetError?: Error | null;
}) {
  const status = searchPhaseStatus || 'NOT_STARTED';
  const needsConfig = status === 'NOT_STARTED';
  const isRunning = status === 'RUNNING' || status === 'READY';
  const isComplete = status === 'COMPLETED' || status === 'EVALUATED';
  const isStarting = isConfiguring || isExecuting;

  // Calculate available criteria sets from completed Phase 1 executions
  const availableCriteriaSets = testRun.executions.filter((e) => e.status === 'COMPLETED').length;
  const maxCriteriaSets = Math.max(1, availableCriteriaSets); // At least 1
  const isStabilityRun = testRun.runType === 'MODEL_STABILITY';

  // For MODEL_STABILITY: Always use ALL iterations to measure consistency impact
  // For MODEL_COMPARISON: Allow user to select, but ensure topN doesn't exceed available
  useEffect(() => {
    if (isStabilityRun) {
      // Stability runs must use ALL iterations
      if (topN !== maxCriteriaSets) {
        setTopN(maxCriteriaSets);
      }
    } else if (topN > maxCriteriaSets) {
      setTopN(maxCriteriaSets);
    }
  }, [topN, maxCriteriaSets, setTopN, isStabilityRun]);

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-gray-200 bg-white p-6">
        <div className="flex items-start gap-4">
          <div className={clsx('rounded-lg p-3',
            isComplete ? 'bg-green-100' :
            isRunning || isStarting ? 'bg-blue-100' :
            'bg-amber-100'
          )}>
            {isComplete ? (
              <CheckCircle className="h-6 w-6 text-green-600" />
            ) : isRunning || isStarting ? (
              <Loader2 className="h-6 w-6 text-blue-600 animate-spin" />
            ) : (
              <Search className="h-6 w-6 text-amber-600" />
            )}
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-bold text-gray-900">Step 3: Care Provider Matching (Phase 4)</h2>
            <p className="mt-1 text-gray-600">
              {testRun.runType === 'MODEL_STABILITY'
                ? 'Configure and match care providers multiple times using the model to evaluate consistency.'
                : 'Configure and match care providers using each AI model based on matching criteria.'}
            </p>
          </div>
        </div>

        {/* Explanation */}
        <div className="mt-6 rounded-lg bg-blue-50 border border-blue-100 p-4">
          <div className="flex items-start gap-2">
            <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="text-sm font-semibold text-blue-900">What happens in this step</h3>
              <ul className="mt-2 space-y-1 text-sm text-blue-800">
                {isStabilityRun ? (
                  <>
                    <li>• Uses <strong>all criteria sets</strong> from Phase 2+3 iterations</li>
                    <li>• Each iteration's criteria is used to match care providers</li>
                    <li>• Results are anonymized (Match 1, Match 2, etc.) to evaluate consistency</li>
                    <li>• This measures how criteria variations affect provider recommendations</li>
                  </>
                ) : (
                  <>
                    <li>• Uses the top-ranked criteria from your evaluation</li>
                    <li>• Each AI model matches care providers using those criteria</li>
                    <li>• Results are anonymized (Match A, Match B, etc.)</li>
                    <li>• Matching runs automatically after you configure settings</li>
                  </>
                )}
              </ul>
            </div>
          </div>
        </div>

        {/* Configuration section - only show if not started */}
        {needsConfig && (
          <>
            {isStabilityRun ? (
              /* MODEL_STABILITY: Use all iterations automatically */
              <div className="mt-6 rounded-lg border border-purple-200 bg-purple-50 p-4">
                <div className="flex items-start gap-3">
                  <Activity className="h-5 w-5 text-purple-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="text-sm font-semibold text-purple-900">All Iterations Required</h3>
                    <p className="mt-1 text-sm text-purple-700">
                      For stability testing, matching will run on <span className="font-bold">all {maxCriteriaSets} iterations</span> from Phase 2+3.
                      This allows comparing how variations in criteria generation affect the recommended care providers.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              /* MODEL_COMPARISON: Allow user to select topN */
              <div className="mt-6 rounded-lg border border-gray-200 p-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  How many top criteria sets to use for matching?
                </label>
                <p className="text-xs text-gray-500 mb-4">
                  Choose how many of the best-ranked criteria from Step 2 should be used for matching.
                  <span className="font-medium text-gray-600"> ({availableCriteriaSets} available)</span>
                </p>
                <div className="flex items-center gap-6">
                  <input
                    type="range"
                    min="1"
                    max={maxCriteriaSets}
                    value={topN}
                    onChange={(e) => setTopN(Number(e.target.value))}
                    className="flex-1 max-w-[200px] accent-amber-600 h-2"
                    disabled={isStarting || maxCriteriaSets <= 1}
                  />
                  <div className="flex items-baseline gap-1">
                    <span className="text-3xl font-bold text-gray-900">{topN}</span>
                    <span className="text-sm text-gray-500">of {maxCriteriaSets} criteria set{maxCriteriaSets > 1 ? 's' : ''}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Model Selection - for comparison runs, allow selecting which models */}
            {!isStabilityRun && (
              <div className="mt-4 rounded-lg border border-gray-200 p-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Select Matching Models
                </label>
                <p className="text-xs text-gray-500 mb-3">
                  Choose which AI models will perform the care provider matching.
                </p>
                <div className="flex flex-wrap gap-2">
                  {ALL_SEARCH_MODELS.map((model) => {
                    const isSelected = selectedSearchModels.includes(model.id);
                    return (
                      <button
                        key={model.id}
                        type="button"
                        onClick={() => {
                          setSelectedSearchModels(
                            isSelected
                              ? selectedSearchModels.filter((id) => id !== model.id)
                              : [...selectedSearchModels, model.id]
                          );
                        }}
                        disabled={isStarting}
                        className={clsx(
                          'inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-all',
                          isSelected
                            ? clsx(model.color, 'border-current ring-2 ring-current/20')
                            : 'bg-gray-50 text-gray-400 border-gray-200 hover:bg-gray-100',
                          isStarting && 'opacity-50 cursor-not-allowed'
                        )}
                      >
                        <span className={clsx(
                          'w-4 h-4 rounded border flex items-center justify-center text-xs',
                          isSelected ? 'bg-current/20 border-current' : 'border-gray-300'
                        )}>
                          {isSelected && '✓'}
                        </span>
                        {model.name}
                      </button>
                    );
                  })}
                </div>
                {selectedSearchModels.length === 0 && (
                  <p className="mt-2 text-xs text-red-500">Please select at least one model</p>
                )}
              </div>
            )}

            <div className="mt-4 rounded-lg bg-gray-50 p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Matching Summary</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">{isStabilityRun ? 'Iterations to match:' : 'Matching Criteria sets to use:'}</span>
                  <span className="font-medium text-gray-900">{topN} of {availableCriteriaSets}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">{isStabilityRun ? 'Model being tested:' : 'AI models:'}</span>
                  <span className="font-medium text-gray-900">{isStabilityRun ? 1 : selectedSearchModels.length}</span>
                </div>
                <div className="pt-2 border-t border-gray-200 flex justify-between">
                  <span className="text-gray-700 font-medium">Total matches:</span>
                  {/* For stability: iterations × 1 model; for comparison: topN × selected models */}
                  <span className="font-bold text-amber-600">{isStabilityRun ? topN : topN * selectedSearchModels.length}</span>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {/* For stability runs, only show the first model (the one being tested) */}
                {/* For comparison runs, show selected models */}
                {(isStabilityRun ? [testRun.config.models[0]] : selectedSearchModels).map((model) => {
                  const config = MODEL_DISPLAY[model];
                  return (
                    <span key={model} className={clsx('px-2.5 py-1 rounded-md text-xs font-medium', config?.color || 'bg-gray-100 text-gray-700')}>
                      {config?.name || model}
                    </span>
                  );
                })}
              </div>
            </div>
          </>
        )}

        {/* Progress section - show when running or complete */}
        {(isRunning || isComplete || totalSearches > 0) && !needsConfig && (
          <div className="mt-4 rounded-lg border border-gray-200 p-4">
            <div className="flex justify-between text-sm mb-2">
              <span className="font-medium text-gray-700">Matching Progress</span>
              <span className={clsx('font-semibold', isComplete ? 'text-green-600' : 'text-amber-600')}>
                {completedSearches} / {totalSearches || '?'}
              </span>
            </div>
            <div className="h-3 w-full rounded-full bg-gray-200 overflow-hidden">
              <div
                className={clsx(
                  'h-3 rounded-full transition-all duration-500',
                  isComplete ? 'bg-green-500' : 'bg-amber-500'
                )}
                style={{ width: `${totalSearches > 0 ? (completedSearches / totalSearches) * 100 : 0}%` }}
              />
            </div>
            {isRunning && (
              <SearchProgressMessages completedSearches={completedSearches} totalSearches={totalSearches} runType={testRun.runType} />
            )}
            {isComplete && (
              <div className="mt-3 flex items-center gap-2 text-sm text-green-600">
                <CheckCircle className="h-4 w-4" />
                <span>
                  {completedSearches === totalSearches
                    ? 'All searches complete! Ready for evaluation.'
                    : `${completedSearches} of ${totalSearches} searches complete. Ready for evaluation.`}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Error display */}
        {error && needsConfig && (
          <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-4">
            <div className="flex items-center gap-2 text-red-700">
              <AlertCircle className="h-5 w-5" />
              <span className="font-medium">Matching failed</span>
            </div>
            <p className="mt-1 text-sm text-red-600">{error.message || 'Please try again'}</p>
          </div>
        )}
        {resetError && (
          <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-4">
            <div className="flex items-center gap-2 text-red-700">
              <AlertCircle className="h-5 w-5" />
              <span className="font-medium">Reset failed</span>
            </div>
            <p className="mt-1 text-sm text-red-600">{resetError.message || 'Please try again'}</p>
          </div>
        )}

        {/* Action section */}
        <div className="mt-6 pt-4 border-t border-gray-200">
          {needsConfig && (
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-900">Ready to match?</p>
                <p className="text-xs text-gray-500">Matching will start automatically after configuration</p>
              </div>
              <button
                onClick={onConfigureAndStart}
                disabled={isStarting || (!isStabilityRun && selectedSearchModels.length === 0)}
                className="inline-flex items-center gap-2 rounded-lg bg-green-600 px-6 py-3 text-sm font-semibold text-white shadow-sm hover:bg-green-700 disabled:opacity-50 transition-colors"
              >
                {isStarting ? <Loader2 className="h-5 w-5 animate-spin" /> : <Search className="h-5 w-5" />}
                {isStarting ? 'Starting...' : 'Start Matching'}
              </button>
            </div>
          )}
          {isRunning && !needsConfig && (
            <div className="flex items-center gap-3 rounded-lg bg-blue-50 border border-blue-200 p-4">
              <Clock className="h-5 w-5 text-blue-600" />
              <div>
                <p className="text-sm font-medium text-blue-900">Matching in progress</p>
                <p className="text-xs text-blue-700">Please wait while the AI models match providers. You can leave this page and come back.</p>
              </div>
            </div>
          )}
          {isComplete && (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-green-600">
                <CheckCircle className="h-5 w-5" />
                <span className="font-medium">Step complete!</span>
              </div>
              <div className="flex items-center gap-3">
                {onReset && (
                  <button
                    onClick={onReset}
                    disabled={isResetting}
                    className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50 transition-colors"
                  >
                    {isResetting ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <RotateCcw className="h-4 w-4" />
                    )}
                    Reset & Retry
                  </button>
                )}
                <Link
                  to={isStabilityRun
                    ? `/validation/test-runs/${testRun.id}/search-stability-evaluate`
                    : `/validation/test-runs/${testRun.id}/search-evaluate`}
                  className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 transition-colors"
                >
                  <Search className="h-4 w-4" />
                  View Search Results
                </Link>
                <button
                  onClick={onNext}
                  className="inline-flex items-center gap-2 rounded-lg bg-amber-600 px-6 py-3 text-sm font-semibold text-white shadow-sm hover:bg-amber-700 transition-colors"
                >
                  Continue to Matching Evaluation
                  <ArrowRight className="h-5 w-5" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Phase2EvaluateContent({
  testRunId,
  isComplete,
  runType,
  onNext,
}: {
  testRunId: string;
  isComplete: boolean;
  runType: RunType;
  onNext: () => void;
}) {
  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-gray-200 bg-white p-6">
        <div className="flex items-start gap-4">
          <div className={clsx('rounded-lg p-3', isComplete ? 'bg-green-100' : 'bg-amber-100')}>
            {isComplete ? (
              <CheckCircle className="h-6 w-6 text-green-600" />
            ) : (
              <Eye className="h-6 w-6 text-amber-600" />
            )}
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-bold text-gray-900">Step 4: Evaluate Matching Results</h2>
            <p className="mt-1 text-gray-600">
              {runType === 'MODEL_STABILITY'
                ? 'Review and evaluate the provider matching results from different iterations. You won\'t know which iteration produced each result (blind evaluation).'
                : 'Review and rank the provider matching results. You won\'t know which model produced each result (blind evaluation).'}
            </p>
          </div>
        </div>

        {/* What you need to do */}
        <div className="mt-6 rounded-lg bg-amber-50 border border-amber-100 p-4">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="text-sm font-semibold text-amber-900">Your task in this step</h3>
              <p className="mt-1 text-sm text-amber-800">
                {runType === 'MODEL_STABILITY'
                  ? 'Compare the matching results from different iterations (shown as "Match 1", "Match 2", etc.) and evaluate consistency.'
                  : 'Compare the matching results from different models (shown as "Match A", "Match B", etc.) and rank them based on quality.'}
              </p>
            </div>
          </div>
        </div>

        {/* Evaluation criteria - different for stability vs comparison */}
        <div className="mt-4 rounded-lg bg-gray-50 p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">
            {runType === 'MODEL_STABILITY' ? 'You\'ll evaluate provider consistency on:' : 'You\'ll evaluate each match on:'}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {runType === 'MODEL_STABILITY' ? (
              // Stability-specific criteria for Phase 2
              <>
                <div className="flex items-start gap-2">
                  <div className="rounded-full bg-purple-100 p-1 mt-0.5">
                    <CheckCircle className="h-3 w-3 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-800">Provider Overlap</p>
                    <p className="text-xs text-gray-500">Are the same providers found across iterations?</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="rounded-full bg-purple-100 p-1 mt-0.5">
                    <CheckCircle className="h-3 w-3 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-800">Ranking Consistency</p>
                    <p className="text-xs text-gray-500">Are providers ranked similarly?</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="rounded-full bg-purple-100 p-1 mt-0.5">
                    <CheckCircle className="h-3 w-3 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-800">Top-N Agreement</p>
                    <p className="text-xs text-gray-500">Do iterations agree on top providers?</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="rounded-full bg-purple-100 p-1 mt-0.5">
                    <CheckCircle className="h-3 w-3 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-800">Overall Stability</p>
                    <p className="text-xs text-gray-500">Rate matching consistency overall</p>
                  </div>
                </div>
              </>
            ) : (
              // Model comparison criteria for Phase 2
              <>
                <div className="flex items-start gap-2">
                  <div className="rounded-full bg-blue-100 p-1 mt-0.5">
                    <CheckCircle className="h-3 w-3 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-800">Provider Relevance</p>
                    <p className="text-xs text-gray-500">Are the found providers appropriate?</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="rounded-full bg-blue-100 p-1 mt-0.5">
                    <CheckCircle className="h-3 w-3 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-800">Geographic Accuracy</p>
                    <p className="text-xs text-gray-500">Are providers in the right location?</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="rounded-full bg-blue-100 p-1 mt-0.5">
                    <CheckCircle className="h-3 w-3 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-800">Specialty Matching</p>
                    <p className="text-xs text-gray-500">Do specialties match the criteria?</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="rounded-full bg-blue-100 p-1 mt-0.5">
                    <CheckCircle className="h-3 w-3 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-800">Overall Ranking</p>
                    <p className="text-xs text-gray-500">Which search result is best?</p>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Action section */}
        <div className="mt-6 pt-4 border-t border-gray-200">
          {isComplete ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-green-600">
                <CheckCircle className="h-5 w-5" />
                <span className="font-medium">Evaluation complete!</span>
              </div>
              <div className="flex items-center gap-3">
                <Link
                  to={runType === 'MODEL_STABILITY'
                    ? `/validation/test-runs/${testRunId}/search-stability-evaluate`
                    : `/validation/test-runs/${testRunId}/search-evaluate`}
                  className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 transition-colors"
                >
                  <Eye className="h-4 w-4" />
                  View Evaluation
                </Link>
                <button
                  onClick={onNext}
                  className="inline-flex items-center gap-2 rounded-lg bg-amber-600 px-6 py-3 text-sm font-semibold text-white shadow-sm hover:bg-amber-700 transition-colors"
                >
                  Continue to Quality Assessment
                  <ArrowRight className="h-5 w-5" />
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-900">Ready to evaluate?</p>
                <p className="text-xs text-gray-500">
                  {runType === 'MODEL_STABILITY'
                    ? 'Evaluate provider consistency across iterations'
                    : 'Estimated time: 5-10 minutes'}
                </p>
              </div>
              <Link
                to={runType === 'MODEL_STABILITY'
                  ? `/validation/test-runs/${testRunId}/search-stability-evaluate`
                  : `/validation/test-runs/${testRunId}/search-evaluate`}
                className="inline-flex items-center gap-2 rounded-lg bg-amber-600 px-6 py-3 text-sm font-semibold text-white shadow-sm hover:bg-amber-700 transition-colors"
              >
                <Eye className="h-5 w-5" />
                {runType === 'MODEL_STABILITY' ? 'Start Stability Evaluation' : 'Start Matching Evaluation'}
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Phase5QualityAssessmentContent({
  testRunId,
  isComplete,
  onNext,
}: {
  testRunId: string;
  isComplete: boolean;
  onNext: () => void;
}) {
  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-gray-200 bg-white p-6">
        <div className="flex items-start gap-4">
          <div className={clsx('rounded-lg p-3', isComplete ? 'bg-green-100' : 'bg-yellow-100')}>
            {isComplete ? (
              <CheckCircle className="h-6 w-6 text-green-600" />
            ) : (
              <Star className="h-6 w-6 text-yellow-600" />
            )}
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-bold text-gray-900">Step 5: Provider Quality Assessment</h2>
            <p className="mt-1 text-gray-600">
              Assess the quality of recommended care providers across all matching results using a structured 6-dimension scoring framework.
            </p>
          </div>
        </div>

        {/* What this step involves */}
        <div className="mt-6 rounded-lg bg-yellow-50 border border-yellow-100 p-4">
          <div className="flex items-start gap-2">
            <Info className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="text-sm font-semibold text-yellow-900">What you&apos;ll do in this step</h3>
              <p className="mt-1 text-sm text-yellow-800">
                Evaluate each consolidated provider recommendation using a comprehensive quality framework. This helps measure how well the AI models matched patients with appropriate care providers.
              </p>
            </div>
          </div>
        </div>

        {/* Quality dimensions overview */}
        <div className="mt-4 rounded-lg bg-gray-50 p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Quality Assessment Dimensions</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="flex items-start gap-2">
              <div className="rounded-full bg-blue-100 p-1 mt-0.5">
                <CheckCircle className="h-3 w-3 text-blue-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-800">Medical Fit</p>
                <p className="text-xs text-gray-500">Does the provider match the medical needs?</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <div className="rounded-full bg-blue-100 p-1 mt-0.5">
                <CheckCircle className="h-3 w-3 text-blue-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-800">Urgency & Timing</p>
                <p className="text-xs text-gray-500">Can they accommodate the patient&apos;s timeline?</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <div className="rounded-full bg-blue-100 p-1 mt-0.5">
                <CheckCircle className="h-3 w-3 text-blue-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-800">Therapy Pathway</p>
                <p className="text-xs text-gray-500">Does their approach fit the patient&apos;s needs?</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <div className="rounded-full bg-blue-100 p-1 mt-0.5">
                <CheckCircle className="h-3 w-3 text-blue-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-800">Complexity & Risk</p>
                <p className="text-xs text-gray-500">Can they handle the case complexity?</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <div className="rounded-full bg-blue-100 p-1 mt-0.5">
                <CheckCircle className="h-3 w-3 text-blue-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-800">Outcome Fit</p>
                <p className="text-xs text-gray-500">Can they meet the patient&apos;s expectations?</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <div className="rounded-full bg-blue-100 p-1 mt-0.5">
                <CheckCircle className="h-3 w-3 text-blue-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-800">Patient Centricity</p>
                <p className="text-xs text-gray-500">Are special considerations addressed?</p>
              </div>
            </div>
          </div>
        </div>

        {/* Action section */}
        <div className="mt-6 pt-4 border-t border-gray-200">
          {isComplete ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-green-600">
                <CheckCircle className="h-5 w-5" />
                <span className="font-medium">Quality assessment complete!</span>
              </div>
              <div className="flex items-center gap-3">
                <Link
                  to={`/validation/test-runs/${testRunId}/quality-assessment`}
                  className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 transition-colors"
                >
                  <Star className="h-4 w-4" />
                  View Assessments
                </Link>
                <button
                  onClick={onNext}
                  className="inline-flex items-center gap-2 rounded-lg bg-amber-600 px-6 py-3 text-sm font-semibold text-white shadow-sm hover:bg-amber-700 transition-colors"
                >
                  Continue to Final Results
                  <ArrowRight className="h-5 w-5" />
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-900">Ready to assess providers?</p>
                <p className="text-xs text-gray-500">Estimated time: 10-15 minutes</p>
              </div>
              <Link
                to={`/validation/test-runs/${testRunId}/quality-assessment`}
                className="inline-flex items-center gap-2 rounded-lg bg-yellow-600 px-6 py-3 text-sm font-semibold text-white shadow-sm hover:bg-yellow-700 transition-colors"
              >
                <Star className="h-5 w-5" />
                Start Quality Assessment
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AnalysisContent({ testRunId, isRevealed, runType, onReveal }: { testRunId: string; isRevealed: boolean; runType: RunType; onReveal: () => void }) {
  const navigate = useNavigate();
  const [isRevealing, setIsRevealing] = useState(false);
  const [revealError, setRevealError] = useState<string | null>(null);

  const handleRevealAndNavigate = async () => {
    // Determine the correct results page based on run type
    const resultsPath = runType === 'MODEL_STABILITY'
      ? `/validation/test-runs/${testRunId}/stability-results`
      : `/validation/test-runs/${testRunId}/results`;

    if (isRevealed) {
      // Already revealed, just navigate to results
      navigate(resultsPath);
      return;
    }

    setIsRevealing(true);
    setRevealError(null);
    try {
      await api.post(`/api/validation/test-runs/${testRunId}/reveal`, {});
      onReveal();
      // Navigate directly to results after revealing
      navigate(resultsPath);
    } catch (err) {
      setRevealError(err instanceof Error ? err.message : 'Failed to reveal results');
      setIsRevealing(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-gray-200 bg-white p-6">
        <div className="flex items-start gap-4">
          <div className={clsx('rounded-lg p-3', isRevealed ? 'bg-green-100' : 'bg-purple-100')}>
            {isRevealed ? (
              <CheckCircle className="h-6 w-6 text-green-600" />
            ) : (
              <Trophy className="h-6 w-6 text-purple-600" />
            )}
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-bold text-gray-900">Step 5: Final Results & Analysis</h2>
            <p className="mt-1 text-gray-600">
              {runType === 'MODEL_STABILITY'
                ? 'Validation complete! You can now reveal iteration details and see consistency metrics.'
                : 'Validation complete! You can now reveal the model identities and see who performed best.'}
            </p>
          </div>
        </div>

        {/* What happens in this step */}
        <div className="mt-6 rounded-lg bg-blue-50 border border-blue-100 p-4">
          <div className="flex items-start gap-2">
            <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="text-sm font-semibold text-blue-900">What you'll see</h3>
              <ul className="mt-2 space-y-1 text-sm text-blue-800">
                {runType === 'MODEL_STABILITY' ? (
                  <>
                    <li>• Which iteration was "Run 1", "Run 2", etc.</li>
                    <li>• Consistency analysis across all iterations</li>
                    <li>• Variance in indication analysis</li>
                    <li>• Variance in provider matching results</li>
                  </>
                ) : (
                  <>
                    <li>• Which AI model was "Model A", "Model B", etc.</li>
                    <li>• Performance rankings from your blind evaluations</li>
                    <li>• Comparison of indication analysis quality</li>
                    <li>• Comparison of provider matching quality</li>
                  </>
                )}
              </ul>
            </div>
          </div>
        </div>

        {!isRevealed ? (
          <>
            {/* Warning about reveal action */}
            <div className="mt-4 rounded-lg bg-amber-50 border border-amber-200 p-4">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="text-sm font-semibold text-amber-900">Important: This action cannot be undone</h3>
                  <p className="mt-1 text-sm text-amber-800">
                    {runType === 'MODEL_STABILITY'
                      ? 'Once you reveal the iteration details, the blind evaluation period ends. Make sure you\'ve completed all evaluations before revealing.'
                      : 'Once you reveal the model identities, the blind evaluation period ends. Make sure you\'ve completed all evaluations before revealing.'}
                  </p>
                </div>
              </div>
            </div>

            {revealError && (
              <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-4">
                <div className="flex items-center gap-2 text-red-700">
                  <AlertCircle className="h-5 w-5" />
                  <span className="font-medium">Reveal failed</span>
                </div>
                <p className="mt-1 text-sm text-red-600">{revealError}</p>
              </div>
            )}

            {/* Action section */}
            <div className="mt-6 pt-4 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-900">Ready to see the results?</p>
                  <p className="text-xs text-gray-500">
                    {runType === 'MODEL_STABILITY'
                      ? 'This will reveal iteration details and show the consistency analysis'
                      : 'This will reveal model identities and show the final comparison'}
                  </p>
                </div>
                <button
                  onClick={handleRevealAndNavigate}
                  disabled={isRevealing}
                  className="inline-flex items-center gap-2 rounded-lg bg-purple-600 px-6 py-3 text-sm font-semibold text-white shadow-sm hover:bg-purple-700 disabled:opacity-50 transition-colors"
                >
                  {isRevealing ? <Loader2 className="h-5 w-5 animate-spin" /> : <BarChart3 className="h-5 w-5" />}
                  {isRevealing ? 'Revealing...' : 'Reveal & View Results'}
                </button>
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Already revealed - show link to results */}
            <div className="mt-4 rounded-lg bg-green-50 border border-green-200 p-4">
              <div className="flex items-start gap-2">
                <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="text-sm font-semibold text-green-900">
                    {runType === 'MODEL_STABILITY' ? 'Stability Validation Complete!' : 'Model Validation Complete!'}
                  </h3>
                  <p className="mt-1 text-sm text-green-800">
                    {runType === 'MODEL_STABILITY'
                      ? 'Iteration details have been revealed. View the results to see consistency analysis.'
                      : 'Model identities have been revealed. View the results to see performance comparison.'}
                  </p>
                </div>
              </div>
            </div>

            {/* Action section */}
            <div className="mt-6 pt-4 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-green-600">
                  <Trophy className="h-5 w-5" />
                  <span className="font-medium">All steps complete!</span>
                </div>
                <button
                  onClick={handleRevealAndNavigate}
                  className="inline-flex items-center gap-2 rounded-lg bg-purple-600 px-6 py-3 text-sm font-semibold text-white shadow-sm hover:bg-purple-700 transition-colors"
                >
                  <BarChart3 className="h-5 w-5" />
                  View Results
                  <ArrowRight className="h-5 w-5" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
